import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@parcel/ui';
import { AppLogo } from '@parcel/ui/app-logo';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import * as React from 'react';
import type { ApiContactStatus, ApiContactSummary } from '../../lib/contacts.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { formatRelativeDate } from '../../lib/mock-accounts.js';
import type { Score } from '../../lib/scores.js';
import { CONTACTS_DEFAULT_HIDDEN } from '../../lib/table-views.js';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE,
  type PinnedColumn,
} from '../data-table/data-table-layout.js';
import { PersonaTag } from '../enrich/persona-tag.js';
import { EmailCell, PhoneCell } from '../enrich/reach-cell.js';
import { scoreColumn } from '../scoring/score-column.js';
import { ContactAvatar } from './contact-avatar.js';
import { ContactRoleBadges, getContactRoleMeta } from './contact-role-badges.js';

export type ContactStatus = ApiContactStatus;
export type ContactRow = ApiContactSummary;

export const CONTACTS_PINNED_COLUMNS: PinnedColumn[] = [
  { id: 'select', left: 0, width: 52 },
  { id: 'name', left: 52, width: 280, last: true },
];

export const CONTACTS_DEFAULT_SORTING: SortingState = [{ id: 'updatedAt', desc: true }];

export interface ContactsTableOptions {
  browseMode: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** The workspace's Prospect Score, for its bucket labels. */
  prospectScore?: Score;
}

function contactColumns(browseMode: boolean, prospectScore?: Score): ColumnDef<ContactRow>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={
              browseMode
                ? table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && 'indeterminate')
                : table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              browseMode
                ? table.toggleAllRowsSelected(Boolean(value))
                : table.toggleAllPageRowsSelected(Boolean(value))
            }
            aria-label={browseMode ? 'Select all loaded contacts' : 'Select all'}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${row.original.name}`}
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 52,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Contact" />,
      cell: ({ row }) => {
        const linkedin = normalizeExternalUrl(row.original.linkedin_url);
        return (
          <div className="flex min-w-0 items-center gap-3">
            <ContactAvatar
              name={row.original.name}
              accountWebsite={row.original.account_website}
              email={row.original.email}
              size="sm"
            />
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
              {row.original.name}
            </span>
            {linkedin ? (
              <a
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                aria-label={`${row.original.name} on LinkedIn`}
                className="inline-flex shrink-0 opacity-50 grayscale transition hover:opacity-100 hover:grayscale-0"
              >
                <AppLogo name="linkedin" size={14} title="LinkedIn" />
              </a>
            ) : null}
          </div>
        );
      },
      size: 280,
    },
    scoreColumn<ContactRow>('contact', prospectScore),
    {
      id: 'persona',
      header: 'Persona',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.reach?.persona ? (
          <PersonaTag
            family={row.original.reach.persona}
            match={row.original.reach.persona_match}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      size: 170,
    },
    {
      accessorKey: 'title',
      id: 'title',
      header: 'Title',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="truncate text-sm text-foreground">{row.original.title ?? '—'}</div>
      ),
      size: 220,
    },
    {
      id: 'email',
      header: 'Email',
      enableSorting: false,
      cell: ({ row }) => <EmailCell contact={row.original} variant="table" />,
      size: 280,
    },
    {
      id: 'phone',
      header: 'Phone',
      enableSorting: false,
      cell: ({ row }) => <PhoneCell contact={row.original} />,
      size: 180,
    },
    {
      accessorKey: 'account_name',
      id: 'account_name',
      header: 'Account',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="truncate text-sm text-foreground">{row.original.account_name}</div>
      ),
      size: 200,
    },
    {
      accessorKey: 'roles',
      id: 'roles',
      header: 'Type',
      enableSorting: false,
      // Capped + fixed-width so multi-role contacts can't stretch the column
      // (labels are long, so collapse to one badge + "+N").
      cell: ({ row }) => (
        <ContactRoleBadges roles={row.original.roles} maxVisible={1} className="flex-nowrap" />
      ),
      size: 200,
    },
    {
      accessorKey: 'updated_at',
      id: 'updatedAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Updated" />,
      cell: ({ row }) => (
        <div className="text-sm">{formatRelativeDate(row.original.updated_at)}</div>
      ),
      size: 140,
    },
    {
      id: 'actions',
      enableHiding: false,
      enableSorting: false,
      // The 32px button and the cell padding overflowed 56px, which drew a second ellipsis.
      size: 64,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open row actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem>Open contact</DropdownMenuItem>
            <DropdownMenuItem>Open account</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                row.toggleSelected(true);
              }}
            >
              Add to View
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}

export { getContactRoleMeta };

export function getContactTableColumnIds() {
  return contactColumns(false).map(getContactTableColumnId);
}

function getContactTableColumnId(column: ColumnDef<ContactRow>) {
  if ('id' in column && column.id) return column.id;
  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return column.accessorKey;
  }
  return '';
}

export function useContactsTable(contacts: ContactRow[], options: ContactsTableOptions) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => ({
    ...CONTACTS_DEFAULT_HIDDEN,
  }));
  const [rowSelection, setRowSelection] = React.useState({});
  const { browseMode, prospectScore } = options;
  const columns = React.useMemo(
    () => contactColumns(browseMode, prospectScore),
    [browseMode, prospectScore],
  );

  const table = useReactTable({
    data: contacts,
    columns,
    // Key rows by contact id (not array index) so rows reorder instead of
    // re-mounting as the list grows or re-sorts while pages stream in.
    getRowId: (row) => row.id,
    state: {
      sorting: options.sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE,
      },
    },
    enableRowSelection: true,
    onSortingChange: options.onSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: options.browseMode,
    getPaginationRowModel: options.browseMode ? undefined : getPaginationRowModel(),
    getSortedRowModel: options.browseMode ? undefined : getSortedRowModel(),
  });

  return { table };
}
